import React, { useState } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { toast } from 'react-toastify';
import { API_URL } from '../../../constants/string-constants';

const GroupsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 80px 16px; /* Extra bottom padding for floating button */
  flex: 1;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--clr-bg-secondary, #2a2a2a);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 2px;
  }
`;

const GroupItem = styled.div`
  background-color: var(--clr-bg-tertiary, #333);
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  transition: background-color 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
`;

const GroupInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const GroupName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: var(--clr-text, #ffffff);
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: ${(props) => (props.$muted ? 0.6 : 1)};
`;

const MutedIcon = styled.span`
  font-size: 12px;
  opacity: 0.8;
`;

const GroupMembers = styled.div`
  font-size: 12px;
  color: var(--clr-text-secondary, #aaa);
  margin-bottom: 4px;
`;

const LastMessage = styled.div`
  font-size: 12px;
  color: var(--clr-text-secondary, #aaa);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UnreadBadge = styled.div`
  background-color: var(--clr-primary, #007bff);
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GroupActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreateGroupButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: var(--clr-primary, #007bff);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 20px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background-color: #0056b3;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const GroupMenuButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
    color: var(--clr-text, #ffffff);
  }
`;

const GroupMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  border: 1px solid var(--clr-border, #444);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
`;

const GroupMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  color: var(--clr-text, #ffffff);
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
  
  &.danger {
    color: var(--clr-danger, #dc3545);
    
    &:hover {
      background-color: rgba(220, 53, 69, 0.1);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--clr-text-secondary, #aaa);
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: var(--clr-text, #ffffff);
`;

const EmptyText = styled.p`
  margin: 0;
  line-height: 1.4;
`;

function GroupsTab({
  groups,
  processing,
  onGroupSelect,
  onShowCreateModal,
  onShowRenameModal,
  onShowAddMembersModal,
  onLeaveGroup,
  onGroupMuteToggled,
  loading
}) {
  const [openGroupMenu, setOpenGroupMenu] = useState(null);
  const [confirmLeaveGroup, setConfirmLeaveGroup] = useState(null);

  const handleToggleMute = async (group) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('Please sign in to mute groups');
      }

      const nextMuted = !group.muted;
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${group.id}/mute`, {
        method: 'PUT',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ muted: nextMuted }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update mute state');
      }

      const analytics = getAnalytics();
      logEvent(analytics, 'group_mute_toggled', {
        muted: nextMuted,
        group_id: group.id,
      });

      toast.success(
        nextMuted ? 'Group muted' : 'Group unmuted'
      );

      if (onGroupMuteToggled) {
        onGroupMuteToggled(group.id, nextMuted);
      }

      setOpenGroupMenu(null);
    } catch (error) {
      console.error('Error toggling group mute:', error);
      toast.error(error.message || 'Failed to update mute state');
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('Please sign in to leave groups');
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${groupId}/leave`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const analytics = getAnalytics();
        logEvent(analytics, 'group_left');

        toast.success(result.message || 'Successfully left the group');
        
        // Notify parent component
        onLeaveGroup(groupId);
        
        setConfirmLeaveGroup(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to leave group');
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error(error.message || 'Failed to leave group');
    }
  };

  if (loading) {
    return (
      <EmptyState>
        <EmptyTitle>Loading groups...</EmptyTitle>
      </EmptyState>
    );
  }

  return (
    <GroupsList>
      {groups.length === 0 ? (
        <EmptyState>
          <EmptyTitle>No groups yet</EmptyTitle>
          <EmptyText>
            Join or create groups to start group messaging!
          </EmptyText>
        </EmptyState>
      ) : (
        groups.map((group) => (
          <GroupItem key={group.id}>
            <GroupInfo onClick={() => onGroupSelect(group)}>
              <GroupName $muted={group.muted}>
                {group.muted && <MutedIcon title="Muted">🔕</MutedIcon>}
                {group.name || `Group with ${group.member_count} members`}
              </GroupName>
              <GroupMembers>
                {group.member_count} member{group.member_count !== 1 ? 's' : ''}
              </GroupMembers>
              {group.lastMessage && (
                <LastMessage>
                  {group.lastMessage.isFromMe ? 'You: ' : ''}
                  {group.lastMessage.messageText}
                </LastMessage>
              )}
            </GroupInfo>
            <GroupActions>
              {!group.muted && group.unreadCount > 0 && (
                <UnreadBadge>{group.unreadCount}</UnreadBadge>
              )}
              <div style={{ position: 'relative' }} data-group-menu>
                <GroupMenuButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenGroupMenu(openGroupMenu === group.id ? null : group.id);
                  }}
                >
                  ⋮
                </GroupMenuButton>
                {openGroupMenu === group.id && (
                  <GroupMenuDropdown>
                    <GroupMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowRenameModal(group);
                        setOpenGroupMenu(null);
                      }}
                      disabled={processing.has(group.id)}
                    >
                      Rename Group
                    </GroupMenuItem>
                    <GroupMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onShowAddMembersModal(group.id);
                        setOpenGroupMenu(null);
                      }}
                      disabled={processing.has(group.id)}
                    >
                      Add Members
                    </GroupMenuItem>
                    <GroupMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleMute(group);
                      }}
                      disabled={processing.has(group.id)}
                    >
                      {group.muted ? 'Unmute Notifications' : 'Mute Notifications'}
                    </GroupMenuItem>
                    {confirmLeaveGroup === group.id ? (
                      <>
                        <GroupMenuItem
                          className="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeaveGroup(group.id);
                            setOpenGroupMenu(null);
                          }}
                          disabled={processing.has(group.id)}
                        >
                          Confirm Leave
                        </GroupMenuItem>
                        <GroupMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmLeaveGroup(null);
                          }}
                        >
                          Cancel
                        </GroupMenuItem>
                      </>
                    ) : (
                      <GroupMenuItem
                        className="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmLeaveGroup(group.id);
                        }}
                        disabled={processing.has(group.id)}
                      >
                        Leave Group
                      </GroupMenuItem>
                    )}
                  </GroupMenuDropdown>
                )}
              </div>
            </GroupActions>
          </GroupItem>
        ))
      )}
      <CreateGroupButton onClick={onShowCreateModal}>
        + Create Group
      </CreateGroupButton>
    </GroupsList>
  );
}

export default GroupsTab;